<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="contract-number-registration-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.contractNumber.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="contract-number-registration-page-title"
        />
      </div>

      <OrganismAlert
        :title="t('subscription.contractNumber.alertInfoSubscriptionLinkedToMove.title')"
        :content-text="
          t('subscription.contractNumber.alertInfoSubscriptionLinkedToMove.description')
        "
        class="my-6"
        data-cy="contract-number-info-alert"
        hasIcon
        state="info"
      />

      <OrganismAlert
        :title="t('subscription.contractNumber.alertSuccessExistingContract.title')"
        :content-text="t('subscription.contractNumber.alertSuccessExistingContract.description')"
        class="mb-6"
        data-cy="contract-number-success-alert"
        hasIcon
        state="success"
      />

      <div>
        <MoleculeLabel
          :text="t('subscription.contractNumber.input.label')"
          class="mt-6 mb-1"
          data-cy="contract-number-input-label"
          label-for="contractNumber"
          mode-helper="tooltip"
          helper-button-aria-label="hover over to display a tooltip"
        >
          <template #tooltip-content-slot>
            {{ t('subscription.contractNumber.input.tooltip') }}
          </template>
        </MoleculeLabel>

        <MoleculeInputText
          id="contract-number"
          data-cy="contract-number-input"
          v-model="contractNumber"
          :helper="contractNumberHelperMessage"
          :input-state="contractNumberInputState"
          :placeholder="t('subscription.contractNumber.input.placeholder')"
          :disabled="isSaveSubscriptionInProgress || isGetSubscriptionInProgress"
          @keydown="onKeyDownContractNumberInput"
          @blur="onBlurContractNumberInput"
          class="w-full"
          name="contractNumber"
        />
      </div>
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { isAxiosError } from 'axios';
import { storeToRefs } from 'pinia';
import { useField } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import { useStepStore, useSubscriptionStore, useToastSnackbarStore } from '@/stores';
import { ToastType } from '@/stores/toastSnackbar.store';

import { getErrorToasterConfig } from '@/utils/error.util';

import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

const { t } = useI18n();

const toastSnackBar = useToastSnackbarStore();
const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();

const { goToNextStep } = stepStore;

const {
  isGetSubscriptionInProgress,
  isGetSubscriptionSuccessfully,
  isSaveSubscriptionInProgress,
  getCurrentSubscriptionId,
  getCurrentSubscriptionActiveContractNumber,
} = storeToRefs(subscriptionStore);

const {
  value: contractNumber,
  errors: contractNumberFormatErrors,
  errorMessage: contractNumberFormatError,
  validate: validateContractNumber,
} = useField<string>('contractNumber', yup.string().required(t('common.forms.required')), {
  initialValue: getCurrentSubscriptionActiveContractNumber.value,
});

const isInvalidContractNumber = ref(false);

const isSubmitted = ref(false);
const hasBlurContractNumberInput = ref(false);

const isSubmittedOrHasBlurContractNumberInput = computed(
  () => isSubmitted.value || hasBlurContractNumberInput.value
);

const contractNumberInputState = computed(() => {
  if (
    isSubmittedOrHasBlurContractNumberInput.value &&
    (contractNumberFormatErrors.value.length > 0 || isInvalidContractNumber.value)
  ) {
    return 'error';
  }

  return 'info';
});

const contractNumberHelperMessage = computed(() => {
  const helper = {
    success: '',
    error: '',
    info: t('subscription.contractNumber.input.helper.info'),
  };

  if (isSubmittedOrHasBlurContractNumberInput.value) {
    if (contractNumberFormatError.value && contractNumberFormatError.value !== '') {
      helper.error = contractNumberFormatError.value;
    }

    if (isInvalidContractNumber.value) {
      helper.error = t('subscription.contractNumber.input.helper.unknownContractNumber');
    }
  }

  return helper;
});

const onBlurContractNumberInput = () => {
  hasBlurContractNumberInput.value = true;
};

const resetContractNumberErrorStatus = () => {
  hasBlurContractNumberInput.value = false;
  isInvalidContractNumber.value = false;
};

const resetSubmit = () => {
  isSubmitted.value = false;
};

const onKeyDownContractNumberInput = () => {
  resetContractNumberErrorStatus();
  resetSubmit();
};

const onSubmit = async () => {
  resetContractNumberErrorStatus();

  isSubmitted.value = true;

  toastSnackBar.clearToasts();

  const contractNumberValidation = await validateContractNumber();

  if (!contractNumberValidation.valid) {
    return;
  }

  if (contractNumber.value === getCurrentSubscriptionActiveContractNumber.value) {
    goToNextStep();
    return;
  }

  try {
    const submitSubscription = getCurrentSubscriptionId.value
      ? subscriptionStore.updateSubscriptionWithContractNumber
      : subscriptionStore.createSubscriptionWithContractNumber;

    await submitSubscription(contractNumber.value);

    goToNextStep();
  } catch (error: unknown) {
    handleContractNumberSubmissionError(error);
  }
};

const handleContractNumberSubmissionError = (error: unknown) => {
  if (!isAxiosError(error)) {
    return;
  }

  switch (error.response?.status) {
    case 422:
      isInvalidContractNumber.value = true;
      break;
    case 500:
      toastSnackBar.addToast({
        id: 'contractNumber',
        title: t('subscription.contractNumber.toast.500.title'),
        content: t('subscription.contractNumber.toast.500.description'),
        timer: 6000,
        toastType: ToastType.Error,
      });
      break;
    default:
      toastSnackBar.addToast(
        getErrorToasterConfig({
          error,
          toasterId: 'contract-number-error',
          defaultMessage: t('subscription.contractNumber.toast.default'),
        })
      );
      break;
  }
};

watch(isGetSubscriptionSuccessfully, isSuccessfully => {
  if (isSuccessfully) {
    contractNumber.value = getCurrentSubscriptionActiveContractNumber.value;
  }
});

onMounted(() => {
  contractNumber.value = getCurrentSubscriptionActiveContractNumber.value || '';
});
</script>

<style scoped></style>
